/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { ReactElement } from "react";

import { useForm } from "react-hook-form";

import * as Yup from "yup";

import { Stack, StackItem } from "@fluentui/react";
import { yupResolver } from "@hookform/resolvers/yup";

import TextFieldController from "Components/ReactHookForm/TextFieldController/TextFieldController";
import { SidePanel } from "Components/SidePanel/SidePanel";

import {
  IAzureBenefitExtendCredit,
  IAzureBenefitExtendCreditFormProps,
} from "./IAzureBenefitExtendCreditFormProps";

export const AzureBenefitExtendCreditForm = (
  props: IAzureBenefitExtendCreditFormProps
): ReactElement => {
  const { isPanelOpen, setIsPanelOpen, isSaving, errorMessage, extendAzureBenefitCredit } = props;

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<IAzureBenefitExtendCredit>({
    //defaultValues,
    resolver: yupResolver(
      Yup.object().shape({
        credit: Yup.number()
          .typeError("Additional Credit must be a number")
          .required("Additional Credit is required")
          .min(1000, "Additional Credit must be greater than or equal to 1000")
          .max(10000, "Additional Credit must be less than or equal to 10000"),
      })
    ),
  });

  const dismissPanel = (): void => {
    setIsPanelOpen(false);
    reset();
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const save = () =>
    handleSubmit((formData) => {
      return extendAzureBenefitCredit(formData.credit);
    });

  return (
    <form onSubmit={save()}>
      <SidePanel
        isOpen={isPanelOpen}
        title="Extend Credit"
        saveButtonLabel="Save"
        error={errorMessage}
        disableActions={isSaving}
        onDismiss={dismissPanel}
        onSave={save()}
      >
        <Stack tokens={{ childrenGap: "1rem" }}>
          <StackItem>You can assign additional credits to this user via the input below</StackItem>
          <StackItem>
            <TextFieldController
              control={control}
              inputName="credit"
              errors={errors}
              isRequired={true}
              textFieldType="number"
              labelDisplayName="Additional Credit"
              prefix="$"
              isDisabled={isSaving}
            />
          </StackItem>
        </Stack>
      </SidePanel>
    </form>
  );
};
